import type { ClickAction, ActionResult } from '../types/actions';
import { waitForElement, scrollIntoView, isElementVisible, isElementEnabled } from '../utils/dom-utils';

export async function executeClickAction(action: ClickAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // Find element
    let element = document.querySelector(action.selector);

    // Wait for element if specified
    if (!element && action.waitForElement) {
      element = await waitForElement(action.selector, action.waitForElement);
    }

    if (!element) {
      return {
        success: false,
        action,
        error: `Element not found: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: false
      };
    }

    // Check if element is visible
    if (!isElementVisible(element)) {
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      const reasons = [];

      if (style.display === 'none') reasons.push('display: none');
      if (style.visibility === 'hidden') reasons.push('visibility: hidden');
      if (style.opacity === '0') reasons.push('opacity: 0');
      if (rect.width === 0 || rect.height === 0) reasons.push(`no dimensions (${rect.width}x${rect.height})`);

      return {
        success: false,
        action,
        error: `Element not visible: ${action.selector}. Reason: ${reasons.join(', ') || 'hidden by parent or CSS'}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Check if element is enabled (for form elements)
    if (!isElementEnabled(element)) {
      return {
        success: false,
        action,
        error: `Element is disabled: ${action.selector}. Element has disabled='true' attribute.`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Scroll into view if specified
    if (action.scrollIntoView !== false) {
      scrollIntoView(element);
      // Wait a bit for scroll to complete
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Store initial state
    const initialUrl = window.location.href;
    const initialScrollY = window.scrollY || window.pageYOffset;

    // Click the element
    if (element instanceof HTMLElement) {
      element.click();
    } else {
      // Fallback for non-HTMLElement (like SVG)
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      element.dispatchEvent(clickEvent);
    }

    // Wait a bit to see if anything changed
    await new Promise(resolve => setTimeout(resolve, 50));

    // Check what changed
    const urlChanged = window.location.href !== initialUrl;
    const scrollChanged = Math.abs((window.scrollY || window.pageYOffset) - initialScrollY) > 5;

    return {
      success: true,
      action,
      timestamp: Date.now(),
      duration: Date.now() - startTime,
      elementFound: true,
      data: {
        elementType: element.tagName.toLowerCase(),
        urlChanged,
        newUrl: urlChanged ? window.location.href : undefined,
        scrollChanged,
        newScrollY: scrollChanged ? (window.scrollY || window.pageYOffset) : undefined
      }
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}
